/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { CoreNetworking } from '@airasia-rn/ui';
import type {
  SearchAggregatorResponse,
  SearchAggregatorInput,
} from '../types/FlightSearch.types';
import EnvironmentURLs, { parseEnv } from '../util/EnvironmentURLs';
import { getApiPlatform, replaceComWithCn } from '../util/utils';

export type Environment = 'stg' | 'prd';

type QueryParams = Record<
  string,
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>
>;

const toQueryString = (queryParams: QueryParams): string => {
  const params = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      params.set(
        key,
        value.filter((v) => v !== null && v !== undefined).join(','),
      );
      return;
    }

    params.set(key, String(value));
  });

  return params.toString();
};

export class SearchAggregatorApiService {
  constructor(
    private env: Environment,
    private geoId?: string,
  ) {}

  private getUrl(): string {
    return replaceComWithCn(
      this.geoId,
      EnvironmentURLs.SEARCH_AGGREGATOR[parseEnv(this.env)],
    );
  }

  async fetchAggregatedResults(params: {
    queryParams: QueryParams;
    body: unknown;
    signal?: AbortSignal;
    headers: SearchAggregatorInput;
    lob?: string;
  }): Promise<SearchAggregatorResponse> {
    const { queryParams, body, signal, headers, lob } = params;

    const platform = getApiPlatform();
    const aggregatorRouteName = lob || (platform === 'web' ? 'web' : 'mobile');
    const baseUrl = this.getUrl().replace('{platform}', aggregatorRouteName);
    const qs = toQueryString(queryParams);
    const url = qs ? `${baseUrl}?${qs}` : baseUrl;

    try {
      const response = await CoreNetworking.post(url, body, {
        signal,
        headers,
      });
      return response.data;
    } catch (err) {
      if (__DEV__) {
        console.log('❌ [SearchAggregatorApi] Fetch ERROR:', err);
      }
      throw err;
    }
  }
}
